if (!customElements.get('product-infinite-scroll')) {
  const infiniteScrollStrings = Object.assign(
    {
      loading: 'Loading more products',
      loaded: 'More products loaded',
      error: 'More products could not be loaded.',
      end: 'All products loaded',
    },
    window.infiniteScrollStrings || {}
  );

  customElements.define(
    'product-infinite-scroll',
    class ProductInfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.requestedUrls = new Set();
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onSentinelIntersection = this.onSentinelIntersection.bind(this);
      }

      connectedCallback() {
        this.grid = document.getElementById(this.dataset.gridId || 'product-grid');
        this.spinner = this.querySelector('[data-infinite-scroll-spinner]');
        this.message = this.querySelector('[data-infinite-scroll-message]');
        this.button = this.querySelector('[data-infinite-scroll-button]');
        this.status = this.querySelector('[data-infinite-scroll-status]');

        if (this.button) this.button.addEventListener('click', this.onButtonClick);

        // The noscript fallback is only useful without JS, and screen readers
        // would otherwise announce a second set of pagination links.
        const noscript = this.querySelector('noscript');
        if (noscript) noscript.remove();

        if (!this.grid || !this.nextUrl) return;

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(this.onSentinelIntersection, {
            rootMargin: '400px 0px 400px 0px',
          });
          this.observer.observe(this);
        } else {
          // Without an observer there is nothing to trigger the load on scroll,
          // so fall back to a button.
          this.toggle(this.button, true);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.button) this.button.removeEventListener('click', this.onButtonClick);
      }

      get nextUrl() {
        return this.dataset.nextUrl || '';
      }

      set nextUrl(value) {
        if (value) {
          this.dataset.nextUrl = value;
        } else {
          delete this.dataset.nextUrl;
        }
      }

      onSentinelIntersection(entries) {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      }

      onButtonClick() {
        this.loadNextPage(true);
      }

      loadNextPage(focusNewProducts = false) {
        const url = this.nextUrl;
        if (this.loading || !url || this.requestedUrls.has(url)) return;

        this.loading = true;
        this.requestedUrls.add(url);
        this.setLoadingState(true);

        fetch(this.buildSectionUrl(url))
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newProducts = this.appendProducts(html, focusNewProducts);
            const loader = html.querySelector('product-infinite-scroll');

            this.nextUrl = loader ? loader.dataset.nextUrl : '';
            this.loading = false;
            this.setLoadingState(false);

            if (!this.nextUrl) {
              this.announce(infiniteScrollStrings.end);
              if (this.observer) this.observer.disconnect();
              return;
            }

            if (newProducts.length === 0) {
              // Nothing came back but more pages are advertised: let the shopper
              // decide whether to keep going instead of looping over empty pages.
              this.toggle(this.button, true);
              return;
            }

            // Re-observing re-evaluates the sentinel, so a short page still
            // triggers the next load without waiting for another scroll.
            if (this.observer) {
              this.observer.unobserve(this);
              this.observer.observe(this);
            }
          })
          .catch(() => {
            this.requestedUrls.delete(url);
            this.loading = false;
            this.setLoadingState(false);
            this.showMessage(infiniteScrollStrings.error);
            this.toggle(this.button, true);
            if (this.observer) this.observer.disconnect();
          });
      }

      buildSectionUrl(url) {
        const sectionUrl = new URL(url, window.location.origin);
        const sectionId = this.grid.dataset.id;
        if (sectionId) sectionUrl.searchParams.set('section_id', sectionId);
        return sectionUrl.toString();
      }

      appendProducts(html, focusNewProducts) {
        const newGrid = html.getElementById(this.dataset.gridId || 'product-grid');
        if (!newGrid) return [];

        const newProducts = Array.from(newGrid.children).map((item) => {
          const product = document.importNode(item, true);
          // Hide before insertion so the reveal animation plays on scroll
          // instead of running off-screen.
          if (product.classList.contains('scroll-trigger')) {
            product.classList.add('scroll-trigger--offscreen');
          }
          return product;
        });

        if (newProducts.length === 0) return newProducts;

        const fragment = document.createDocumentFragment();
        newProducts.forEach((product) => fragment.appendChild(product));
        this.grid.appendChild(fragment);

        this.revealProducts(newProducts);
        this.announce(infiniteScrollStrings.loaded);

        if (focusNewProducts) {
          const firstLink = newProducts[0].querySelector('a, button');
          if (firstLink) firstLink.focus();
        }

        return newProducts;
      }

      revealProducts(products) {
        const animated = products.filter((product) => product.classList.contains('scroll-trigger'));
        if (animated.length === 0) return;

        if (!('IntersectionObserver' in window)) {
          animated.forEach((product) => product.classList.remove('scroll-trigger--offscreen'));
          return;
        }

        const observer = new IntersectionObserver(
          (entries, currentObserver) => {
            entries.forEach((entry, index) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.remove('scroll-trigger--offscreen');
              if (entry.target.hasAttribute('data-cascade')) {
                entry.target.setAttribute('style', `--animation-order: ${index};`);
              }
              currentObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -50px 0px' }
        );

        animated.forEach((product) => observer.observe(product));
      }

      setLoadingState(isLoading) {
        this.toggle(this.spinner, isLoading);
        if (isLoading) {
          this.toggle(this.button, false);
          this.toggle(this.message, false);
          this.announce(infiniteScrollStrings.loading);
        }
      }

      showMessage(text) {
        if (!this.message) return;
        this.message.textContent = text;
        this.toggle(this.message, true);
      }

      announce(text) {
        if (this.status) this.status.textContent = text;
      }

      toggle(element, show) {
        if (!element) return;
        element.toggleAttribute('hidden', !show);
      }
    }
  );
}
